/* boxplot-diagram.ts */
import React from "react";

/* d3.js */
import * as d3 from "d3";

/* Chroma.js */
import chroma from "chroma-js";

/* Helper */
import {
  createColors,
  createGradient,
  createTransformedLabel,
  setTooltipData,
} from "src/components/charts/horizontal-boxplot/helper";

/* Utils */
import { createChartTooltip } from "src/utils/helper/chart-tooltip";

/* Types */
import {
  SVGSelection,
  SVGGSelection,
} from "src/shared/types/d3";

/* Constants */
import { CustomFeature } from "src/shared/constants/common/CustomFeature";

/* Interfaces */
import { BoxplotProps } from "src/shared/interfaces/Boxplot";
import { StatsProps } from "src/shared/interfaces/Boxplot";
import {
  ChartTooltipRef,
  HorizontalBoxplotTooltipProps,
} from "src/shared/interfaces/ChartTooltip";

import { ChartDimensionsProps } from "src/shared/interfaces/charts/BaseChart";

const createViewBox = (
  svg: SVGSelection,
  dimensions: ChartDimensionsProps
) => {
  const { height, width } = dimensions;

  svg
    .attr("viewBox", `0 -20 ${width} ${height + 21}`)
    .attr("preserveAspectRatio", "xMinYMin meet");
};

const computeStats = (
  svg: SVGSelection,
  data: BoxplotProps[],
  gradientPrefix: string,
  customFeature?: CustomFeature
): StatsProps[] => {
  createGradient(svg, data, gradientPrefix, customFeature);

  const stats = data.map((d, i) => {
    const sortedValues = [...d.y].sort((a, b) => a - b);
    const q1 = d3.quantile(sortedValues, 0.25)!;
    const q3 = d3.quantile(sortedValues, 0.75)!;
    const iqr = q3 - q1;
    const lowerFence = q1 - 1.5 * iqr;
    const upperFence = q3 + 1.5 * iqr;

    return {
      category: d.name!,
      min: d3.min(
        sortedValues.filter((v) => v >= lowerFence)
      )!,
      q1,
      median: d3.median(sortedValues)!,
      mean: d3.mean(sortedValues)!,
      q3,
      max: d3.max(
        sortedValues.filter((v) => v <= upperFence)
      )!,
      iqr,
      lowerFence,
      upperFence,
      outliers: sortedValues.filter(
        (v) => v < lowerFence || v > upperFence
      ),
    };
  });

  return stats;
};

const addYGridlines = (
  g: SVGGSelection,
  yScale: d3.ScaleLinear<number, number>,
  width: number
) => {
  g.append("g")
    .attr("class", "grid")
    .call(
      d3
        .axisLeft(yScale)
        .tickSize(-width + 10)
        .tickFormat(() => "")
    )
    .call((g) => g.select(".domain").remove())
    .selectAll("line")
    .attr("x1", 10)
    .attr("stroke", "#ddd")
    .attr("stroke-width", 0.25);
};

const createLayout = (
  svg: SVGSelection,
  data: BoxplotProps[],
  dimensions: ChartDimensionsProps,
  gradientPrefix: string,
  customFeature?: CustomFeature
) => {
  const stats = computeStats(
    svg,
    data,
    gradientPrefix,
    customFeature
  );

  /**
   * For a better readability of the labels,
   * a rotation of 45 degrees is used.
   */
  const longestLabelLength =
    d3.max(stats.map((d) => d.category.length)) ?? 10;
  const estimatedLabelHeight = longestLabelLength * 5;
  const extraMarginForRotation = 108;
  const labelRotation = 45;

  const margin = {
    top: 48,
    right: 32,
    left: 32,
    bottom: Math.max(
      extraMarginForRotation,
      estimatedLabelHeight
    ),
  };

  const { height, width } = dimensions;

  const plotHeight = height - margin.top - margin.bottom;
  const plotWidth = width - margin.left - margin.right;

  const xScale = d3
    .scaleBand()
    .domain(stats.map((d) => d.category))
    .range([0, plotWidth])
    .padding(0.175);

  const yScale = d3
    .scaleLinear()
    .domain([
      d3.min(stats, (d) => d.min)!,
      d3.max(stats, (d) => d.max)!,
    ])
    .nice()
    .range([plotHeight, 0]);

  const g = svg
    .append("g")
    .attr(
      "transform",
      `translate(${margin.left},${margin.top})`
    );

  // x-Axis
  g.append("g")
    .attr("transform", `translate(0,${plotHeight})`)
    .call(d3.axisBottom(xScale).tickSize(0))
    .call((g) => g.select(".domain").remove())
    .selectAll("text")
    .text((d) =>
      createTransformedLabel(String(d), customFeature)
    )
    .attr("transform", `rotate(${labelRotation})`)
    .style("text-anchor", "start")
    .attr("y", 32)
    .style("font-size", "12px")
    .style("fill", "#aab9ce");

  addYGridlines(g, yScale, plotWidth);

  // y-Axis (values)
  g.append("g")
    .call(
      d3
        .axisLeft(yScale)
        .ticks(Math.floor(yScale.domain()[1] / 1))
        .tickFormat(d3.format("d"))
    )
    .call(d3.axisLeft(yScale).tickSize(0))
    .call((g) => g.select(".domain").remove())
    .selectAll("text")
    .style("font-size", "12px")
    .style("fill", "#aab9ce");

  return {
    g,
    xScale,
    yScale,
  };
};

const createBoxplotLayout = (
  svg: SVGSelection,
  tooltipRef: React.RefObject<ChartTooltipRef>,
  data: BoxplotProps[],
  dimension: ChartDimensionsProps,
  gradientPrefix: string,
  customFeature?: CustomFeature
) => {
  const stats = computeStats(
    svg,
    data,
    gradientPrefix,
    customFeature
  );
  const { g, yScale, xScale } = createLayout(
    svg,
    data,
    dimension,
    gradientPrefix,
    customFeature
  );

  const boxWidth = Math.max(xScale.bandwidth() * 0.8, 20);

  stats.map((d, i) => {
    const xPos =
      xScale(d.category)! + xScale.bandwidth() / 2;
    const boxHeight = yScale(d.q1) - yScale(d.q3);
    const color = createColors(d.category, customFeature);

    g.append("rect")
      .attr("x", xPos - boxWidth / 2)
      .attr("y", yScale(d.q3))
      .attr("width", boxWidth)
      .attr("height", isNaN(boxHeight) ? 1 : boxHeight)
      .attr("fill", `url(#${gradientPrefix}-gradient-${i})`)
      .attr("rx", 4)
      .attr("ry", 4)
      .on("mousemove", (event: MouseEvent) => {
        const target = event.target as SVGElement;
        d3.select(target)
          .transition()
          .duration(0)
          .attr("opacity", 0.85);

        const tooltipData: HorizontalBoxplotTooltipProps = {
          min: d.min,
          q1: d.q1,
          median: d.median,
          mean: d.mean,
          q3: d.q3,
          max: d.max,
        };

        createChartTooltip(
          tooltipRef,
          () => setTooltipData(tooltipData),
          svg,
          event,
          {
            margin: -152,
          }
        );
      })
      .on("mouseleave", (event: MouseEvent) => {
        const target = event.target as SVGElement;
        d3.select(target)
          .transition()
          .duration(0)
          .attr("opacity", 1);
        if (tooltipRef.current)
          tooltipRef.current.hideTooltip();
      });

    // Median
    g.append("line")
      .attr("x1", xPos - boxWidth / 2)
      .attr("x2", xPos + boxWidth / 2)
      .attr("y1", yScale(d.median))
      .attr("y2", yScale(d.median))
      .attr("stroke", chroma(color).darken(0.5).hex())
      .attr("stroke-width", 2)
      .style("pointer-events", "none");

    // Whiskers
    g.append("line")
      .attr("x1", xPos)
      .attr("x2", xPos)
      .attr("y1", yScale(d.min))
      .attr("y2", yScale(d.q1))
      .attr("stroke", color);

    // Line
    g.append("line")
      .attr("x1", xPos)
      .attr("x2", xPos)
      .attr("y1", yScale(d.q3))
      .attr("y2", yScale(d.max))
      .attr("stroke", color);

    // Minimum
    g.append("line")
      .attr("x1", xPos - 5)
      .attr("x2", xPos + 5)
      .attr("y1", yScale(d.min))
      .attr("y2", yScale(d.min))
      .attr("stroke", color);

    // Maximum
    g.append("line")
      .attr("x1", xPos - 5)
      .attr("x2", xPos + 5)
      .attr("y1", yScale(d.max))
      .attr("y2", yScale(d.max))
      .attr("stroke", color);

    // Outliers
    d.outliers.forEach((outlier) => {
      g.append("circle")
        .attr("cx", xPos)
        .attr("cy", yScale(outlier))
        .attr("r", 4)
        .attr("fill", color)
        .on("mouseover", (event: MouseEvent) => {
          const target = event.target as SVGElement;
          d3.select(target)
            .transition()
            .duration(0)
            .attr("opacity", 0.85);

          const tooltipProps = {
            outliers: d.outliers[0],
          };

          createChartTooltip(
            tooltipRef,
            () => setTooltipData(tooltipProps),
            svg,
            event,
            {
              margin: -16,
            }
          );
        })
        .on("mouseleave", (event: MouseEvent) => {
          const target = event.target as SVGElement;
          d3.select(target)
            .transition()
            .duration(0)
            .attr("opacity", 1);
          if (tooltipRef.current)
            tooltipRef.current.hideTooltip();
        });
    });
  });
};

export const createBoxplot = (
  svg: SVGSelection,
  data: BoxplotProps[],
  dimension: ChartDimensionsProps,
  tooltipRef: React.RefObject<ChartTooltipRef>,
  gradientPrefix: string,
  customFeature?: CustomFeature
) => {
  if (!svg || !dimension.width) return;

  createViewBox(svg, dimension);
  createBoxplotLayout(
    svg,
    tooltipRef,
    data,
    dimension,
    gradientPrefix,
    customFeature
  );
};
